import { T, useGT } from 'gt-react';
import type { ForecastDetailSourcePosition } from '@erp/rpc';
import { Field } from './DetailPanels';

/**
 * The source system's own position for the same scope, for reconciling against
 * ours.
 *
 * Folded away by default: most of it repeats the stock position and planning
 * levels, and planners read it only when the two disagree. Absent entirely
 * when no location in scope has a row in the source file — an empty block of
 * dashes looked like a broken panel rather than "nothing imported".
 */
export function SourcePositionPanel({
  source,
}: {
  source?: ForecastDetailSourcePosition | null;
}) {
  const gt = useGT();
  if (!source) return null;
  const hint = (meaning: string) =>
    gt('{meaning} Source: GAINS net available file (GSNETDEF).', {
      context: 'forecast source position field hint',
      meaning,
    });
  return (
    <details className="rounded-md border px-3 py-2 text-xs">
      <summary className="cursor-pointer text-xs font-semibold tracking-wide text-muted-foreground uppercase">
        <T context="forecast detail panel title">Source System Position</T>
      </summary>
      <dl className="mt-2 grid max-w-md grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1">
        <Field
          label={<T context="forecast source position field">On Hand</T>}
          value={source.onHand}
          hint={hint(
            gt('On hand as the source system reports it.', {
              context: 'forecast source position field meaning',
            }),
          )}
        />
        <Field
          label={<T context="forecast source position field">Unshipped</T>}
          value={source.unshipped}
          hint={hint(
            gt('Quantity on customer orders not yet shipped (backorders).', {
              context: 'forecast source position field meaning',
            }),
          )}
        />
        <Field
          label={<T context="forecast source position field">On Order</T>}
          value={source.onOrder}
          hint={hint(
            gt('Quantity on open orders to this location.', {
              context: 'forecast source position field meaning',
            }),
          )}
        />
        <Field
          label={
            <T context="forecast source position field">Booked Quantity</T>
          }
          value={source.bookedQty}
          hint={hint(
            gt(
              'Booked quantity. It was 0 on every row of the GAINS file reviewed so far, so treat it as unconfirmed.',
              {
                context: 'forecast source position field meaning',
              },
            ),
          )}
        />
        <Field
          label={<T context="forecast source position field">Net Available</T>}
          value={source.netAvailable}
          hint={gt(
            'On hand minus unshipped, both from the GAINS net available file (GSNETDEF).',
            { context: 'forecast source position field hint' },
          )}
        />
        <Field
          label={<T context="forecast source position field">Min</T>}
          value={source.min}
          hint={hint(
            gt('The min currently held in the source system.', {
              context: 'forecast source position field meaning',
            }),
          )}
        />
        <Field
          label={<T context="forecast source position field">Max</T>}
          value={source.max}
          hint={hint(
            gt('The max currently held in the source system.', {
              context: 'forecast source position field meaning',
            }),
          )}
        />
      </dl>
      <p className="mt-1 text-muted-foreground">
        <T context="forecast source position read-only explanation">
          Totals across the locations in scope, matched on item number and
          location code. Imported values are read-only; planning overrides do
          not edit the source system.
        </T>
      </p>
    </details>
  );
}
